import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
  Timestamp,
} from "firebase/firestore";
import {
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { db } from "../lib/firebase";
import type { AssessmentResult } from "../types/results";
import { displayErrorMessage } from "../utils/functions";

export interface ResultsContextType {
  saveResults: (results: Omit<AssessmentResult, "id">) => Promise<boolean>;
  getResults: (userId: string, requestingUserId?: string) => Promise<AssessmentResult | null>;
  getAllResults: (userId: string) => Promise<AssessmentResult[]>;
  isLoading: boolean;
  error: string | null;
}

const ResultsContext = createContext<ResultsContextType | undefined>(undefined);
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export function ResultsProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getResults = useCallback(
    async (
      userId: string,
      requestingUserId?: string,
      retryCount = 0
    ): Promise<AssessmentResult | null> => {
      try {
        setIsLoading(true);
        setError(null);

        if (requestingUserId && requestingUserId !== userId) {
          const respondentQuery = query(
            collection(db, "respondents"),
            where("userId", "==", userId),
            where("coachId", "==", requestingUserId)
          );
          const respondentSnapshot = await getDocs(respondentQuery);

          if (respondentSnapshot.empty) {
            throw new Error("Unauthorized access to respondent data");
          }
        }

        const resultsQuery = query(
          collection(db, "results"),
          where("userId", "==", userId)
        );

        const resultsSnapshot = await getDocs(resultsQuery);

        if (resultsSnapshot.empty) {
          return null;
        }

        const resultDoc = [...resultsSnapshot.docs].sort((a, b) =>
          b.data().completedAt.toMillis() - a.data().completedAt.toMillis()
        )[0];
        const data = resultDoc.data();
        return {
          id: resultDoc.id,
          ...data,
          version: data.version || 1,
          completedAt: data.completedAt.toDate(),
          retakenAt: data.retakenAt?.toDate(),
        } as AssessmentResult;
      } catch (err: any) {
        console.error("Error getting results:", err);

        if (retryCount < MAX_RETRIES) {
          await new Promise((resolve) =>
            setTimeout(resolve, RETRY_DELAY * (retryCount + 1))
          );
          return getResults(userId, requestingUserId, retryCount + 1);
        }

        setError(displayErrorMessage(err, "Failed to load assessment results"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const getAllResults = useCallback(
    async (userId: string): Promise<AssessmentResult[]> => {
      try {
        setIsLoading(true);
        setError(null);

        const resultsQuery = query(
          collection(db, "results"),
          where("userId", "==", userId)
        );

        const resultsSnapshot = await getDocs(resultsQuery);

        if (resultsSnapshot.empty) {
          return [];
        }

        return [...resultsSnapshot.docs]
          .sort((a, b) =>
            b.data().completedAt.toMillis() - a.data().completedAt.toMillis()
          )
          .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            version: data.version || 1,
            completedAt: data.completedAt.toDate(),
            retakenAt: data.retakenAt?.toDate(),
          } as AssessmentResult;
          });
      } catch (err: any) {
        console.error("Error getting all results:", err);
        setError(displayErrorMessage(err, "Failed to load assessment results"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const saveResults = useCallback(
    async (results: Omit<AssessmentResult, "id">) => {
      try {
        setIsLoading(true);
        setError(null);

        const respondentsQuery = query(
          collection(db, "respondents"),
          where("userId", "==", results.userId)
        );
        const respondentsSnapshot = await getDocs(respondentsQuery);

        if (respondentsSnapshot.empty) {
          console.error("No respondent record found for user:", results.userId);
          throw new Error("No respondent record found");
        }

        const respondentDoc = respondentsSnapshot.docs[0];
        const respondentData = respondentDoc.data();
        const coachId = respondentData.coachId;

        const isRetake = (respondentData.totalAssessments || 0) > 0;

        let originalResultId: string | null = null;
        if (isRetake) {
          const originalResultQuery = query(
            collection(db, "results"),
            where("userId", "==", results.userId)
          );
          const originalResultSnapshot = await getDocs(originalResultQuery);
          if (!originalResultSnapshot.empty) {
            const originalResult = [...originalResultSnapshot.docs].sort((a, b) =>
              a.data().completedAt.toMillis() - b.data().completedAt.toMillis()
            )[0];
            originalResultId = originalResult.id;
          }
        }

        const now = new Date();
        const docRef = await addDoc(collection(db, "results"), {
          ...results,
          coachId,
          version: isRetake ? (respondentData.totalAssessments + 1) : 1,
          originalResultId: isRetake ? originalResultId : null,
          retakenAt: isRetake ? Timestamp.fromDate(now) : null,
          completedAt: Timestamp.fromDate(now),
        });

        const respondentRef = doc(db, "respondents", respondentDoc.id);
        const newTotalAssessments = (respondentData.totalAssessments || 0) + 1;
        const nextRetakeDate = new Date();
        nextRetakeDate.setDate(nextRetakeDate.getDate() + 7);

        await updateDoc(respondentRef, {
          lastAssessmentDate: Timestamp.fromDate(now),
          totalAssessments: newTotalAssessments,
          retakeCount: isRetake ? (respondentData.retakeCount || 0) + 1 : 0,
          nextRetakeDate: Timestamp.fromDate(nextRetakeDate),
        });

        console.log("Results saved successfully with ID:", docRef.id);
        return true;
      } catch (err: any) {
        console.error("Error saving results:", err);
        setError(displayErrorMessage(err, "Failed to save assessment results"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return (
    <ResultsContext.Provider
      value={{
        saveResults,
        getResults,
        getAllResults,
        isLoading,
        error,
      }}
    >
      {children}
    </ResultsContext.Provider>
  );
}

export function useResults() {
  const context = useContext(ResultsContext);
  if (context === undefined) {
    throw new Error("useResults must be used within a ResultsProvider");
  }
  return context;
}
